import React, { useState, useEffect, useMemo } from 'react';

// "Market This Deal" — the upgraded buyer-blast wizard.
//
// Replaces the old single-purpose BlastBuyersModal. Two screens:
//
//   ┌───────── configure ─────────┐    ┌─────────  preview  ─────────┐
//   │ 1. Pick recipients          │ →  │ Resolved list + samples     │
//   │    • Top N matched           │    │ • email subject + body      │
//   │    • All active              │    │ • SMS body                  │
//   │    • Specific buyers         │    │ • optional custom override  │
//   │    • Lists                   │    │ Simulation status banner    │
//   │ 2. Pick channels (email/SMS) │    │ → Send  (or Back)           │
//   └──────────────────────────────┘    └─────────────────────────────┘
//
// Backend contract:
//   POST /api/deals/:id/marketing-preview  { recipient_mode, top_n, buyer_ids, list_ids, channels }
//     → { recipient_count, with_email, with_phone, recipients, sample, simulation flags }
//   POST /api/deals/:id/blast-buyers       (same body, plus custom_subject/body/sms_body)
//     → { success, buyer_count, email_count, sms_count, simulation flags, followups_scheduled }
//
// Why a preview step: the blast is destructive (real emails + SMS + scheduled follow-ups).
// One Claude sample call before the user commits lets them sanity-check the messaging
// without sending 25–200 emails they can't unsend.

export default function MarketDealModal({ deal, onClose, onSent }) {
  const dealId = deal?.id;

  // ── Step state ─────────────────────────────────────────────────────────────
  const [step, setStep]   = useState('configure'); // configure | preview | sending | done | error
  const [error, setError] = useState(null);

  // ── Configuration (step 1) ─────────────────────────────────────────────────
  const [recipientMode, setRecipientMode] = useState('top_n');
  const [topN, setTopN]                   = useState(25);
  // Pool filter — narrows the top_n candidate pool to one status. '' = no filter
  // (draws from all marketable buyers, current default).
  const [poolStatus, setPoolStatus]       = useState('');
  const [channels, setChannels]           = useState(['email', 'sms']);
  const [selectedBuyerIds, setSelectedBuyerIds] = useState([]);
  const [selectedListIds, setSelectedListIds]   = useState([]);

  // For the individual-buyer picker
  const [allBuyers, setAllBuyers]   = useState(null);  // null = not loaded
  const [buyerSearch, setBuyerSearch] = useState('');

  // For the list picker
  const [allLists, setAllLists] = useState(null);

  // Outbound kill-switch state — fetched on mount so step 1 already knows whether
  // SMS / email will simulate. Updates the channel chip UI + adds banners.
  const [killState, setKillState] = useState(null);

  // ── Preview (step 2) ───────────────────────────────────────────────────────
  const [preview, setPreview]           = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Custom overrides — populated from preview sample, editable, sent on confirm.
  //
  // Defaults to TRUE so blasts use one shared message (cheap: 1 Claude call to draft a
  // sample, then broadcast that text as-is). Untoggling reverts to AI-personalize-per-buyer
  // (expensive: N Claude calls, ~$0.02-0.05 each — a 100-buyer blast costs $2-5).
  // The personalized mode is genuinely better when you're sending a small handpicked list
  // to investor relationships you want to feel custom — but for routine deal blasts to
  // your full list, shared message + STOP/opt-out is the right default.
  const [useCustom, setUseCustom]       = useState(true);
  const [customSubject, setCustomSubject] = useState('');
  const [customBody, setCustomBody]     = useState('');
  const [customSms, setCustomSms]       = useState('');

  // ── Result (after send) ────────────────────────────────────────────────────
  const [result, setResult] = useState(null);

  // ── Side effects ───────────────────────────────────────────────────────────

  // Eager-fetch outbound kill-switch state on mount so step 1 can render accurate
  // channel chips + banners before any preview is requested. Cheap call — no DB / no AI.
  useEffect(() => {
    fetch('/api/deals/marketing/kill-state')
      .then(r => r.json())
      .then(setKillState)
      .catch(() => setKillState({ kill_all_outbound: false, sms_disabled: false, sms_will_simulate: false, email_will_simulate: false }));
  }, []);

  // When SMS_DISABLED flips on after mount, drop SMS from the user's channel selection.
  useEffect(() => {
    if (killState && killState.sms_disabled && channels.includes('sms')) {
      setChannels(c => c.filter(x => x !== 'sms'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [killState && killState.sms_disabled]);

  // Lazy-load buyers when user switches to "Specific buyers" mode
  useEffect(() => {
    if (recipientMode === 'buyer_ids' && allBuyers === null) {
      fetch('/api/buyers?limit=5300')
        .then(r => r.json())
        .then(d => setAllBuyers(Array.isArray(d) ? d : (d.buyers || [])))
        .catch(() => setAllBuyers([]));
    }
    if (recipientMode === 'list_ids' && allLists === null) {
      fetch('/api/lists')
        .then(r => r.json())
        .then(d => setAllLists(Array.isArray(d) ? d : (d.lists || [])))
        .catch(() => setAllLists([]));
    }
  }, [recipientMode, allBuyers, allLists]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const configBody = useMemo(() => ({
    recipient_mode: recipientMode,
    top_n: recipientMode === 'top_n' ? topN : undefined,
    // Only sent when top_n mode + a specific pool is chosen — backend treats absence as "all marketable".
    pool_status: recipientMode === 'top_n' && poolStatus ? poolStatus : undefined,
    buyer_ids: recipientMode === 'buyer_ids' ? selectedBuyerIds : undefined,
    list_ids:  recipientMode === 'list_ids'  ? selectedListIds  : undefined,
    channels,
  }), [recipientMode, topN, poolStatus, selectedBuyerIds, selectedListIds, channels]);

  async function fetchPreview() {
    setPreviewLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/deals/${dealId}/marketing-preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(configBody),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setPreview(data);
      // Seed editable custom fields from the AI sample so user can tweak in-place
      setCustomSubject(data.sample?.email?.subject || '');
      setCustomBody(data.sample?.email?.body || '');
      setCustomSms(data.sample?.sms || '');
      setStep('preview');
    } catch (err) {
      setError(err.message);
      setStep('error');
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleSend() {
    setStep('sending');
    setError(null);
    try {
      const body = { ...configBody };
      if (useCustom) {
        if (channels.includes('email')) {
          body.custom_subject    = customSubject;
          body.custom_email_body = customBody;
        }
        if (channels.includes('sms')) {
          body.custom_sms_body = customSms;
        }
      }
      const r = await fetch(`/api/deals/${dealId}/blast-buyers`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (data.error) throw new Error(data.error);
      setResult(data);
      setStep('done');
      if (onSent) onSent(data);
    } catch (err) {
      setError(err.message);
      setStep('error');
    }
  }

  function toggleChannel(c) {
    setChannels(cur => cur.includes(c) ? cur.filter(x => x !== c) : [...cur, c]);
  }

  function toggleBuyer(id) {
    setSelectedBuyerIds(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  }

  function toggleList(id) {
    setSelectedListIds(cur => cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id]);
  }

  // Filter buyers in the picker
  const filteredBuyers = useMemo(() => {
    if (!allBuyers) return [];
    const q = buyerSearch.trim().toLowerCase();
    if (!q) return allBuyers.slice(0, 100);
    return allBuyers
      .filter(b => `${b.name || ''} ${b.company || ''} ${b.email || ''} ${b.notes || ''}`.toLowerCase().includes(q))
      .slice(0, 100);
  }, [allBuyers, buyerSearch]);

  // Can the user proceed from step 1?
  const canPreview = useMemo(() => {
    if (channels.length === 0) return false;
    if (recipientMode === 'top_n')     return topN > 0;
    if (recipientMode === 'all_active') return true;
    if (recipientMode === 'buyer_ids') return selectedBuyerIds.length > 0;
    if (recipientMode === 'list_ids')  return selectedListIds.length > 0;
    return false;
  }, [recipientMode, topN, channels, selectedBuyerIds, selectedListIds]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={overlay}>
      <div style={modalWrap}>
        <div style={header}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>
              {step === 'done' ? 'Marketing Sent' : 'Market This Deal'}
            </div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
              {deal?.address || 'this deal'}
              {step !== 'done' && step !== 'error' && (
                <span style={{ marginLeft: 10, color: 'var(--gray-500)' }}>
                  · Step {step === 'configure' ? '1 of 2' : '2 of 2'}
                </span>
              )}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <div style={body}>
          {/* ── STEP 1: CONFIGURE ─────────────────────────────────────────── */}
          {step === 'configure' && (
            <>
              <SectionHeader label="Who should we send to?" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                <RadioCard
                  selected={recipientMode === 'top_n'}
                  onSelect={() => setRecipientMode('top_n')}
                  title="Top matched buyers"
                  subtitle="Auto-scored by zip, price, and strategy match against this deal"
                  badge="Recommended"
                  expanded={recipientMode === 'top_n' && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 11, color: 'var(--gray-300)' }}>Top</label>
                        <input
                          type="number"
                          min="1" max="200"
                          value={topN}
                          onChange={e => setTopN(Math.max(1, Math.min(200, parseInt(e.target.value) || 1)))}
                          style={{ ...input, width: 70, padding: '4px 8px' }}
                        />
                        <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>matched buyers (max 200)</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 11, color: 'var(--gray-300)' }}>from pool:</label>
                        <select
                          value={poolStatus}
                          onChange={e => setPoolStatus(e.target.value)}
                          style={{ ...input, padding: '4px 8px', cursor: 'pointer' }}
                        >
                          <option value="">All marketable (Onboarded + Buyer + others)</option>
                          <option value="onboarded">Onboarded only</option>
                          <option value="buyer">Buyer only</option>
                          <option value="new">New leads only</option>
                          <option value="working">Working leads only</option>
                        </select>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 6, lineHeight: 1.4 }}>
                        Narrows the {topN}-buyer candidate pool to one status before ranking. Do-not-contact
                        statuses (blacklisted / unsubscribed / unqualified) are always excluded regardless.
                      </div>
                    </div>
                  )}
                />
                <RadioCard
                  selected={recipientMode === 'all_active'}
                  onSelect={() => setRecipientMode('all_active')}
                  title="All marketable buyers"
                  subtitle="Status: Onboarded or Buyer (new/working not yet onboarded — blacklisted + unsubscribed always excluded). Capped at 200."
                />
                <RadioCard
                  selected={recipientMode === 'buyer_ids'}
                  onSelect={() => setRecipientMode('buyer_ids')}
                  title="Specific buyers"
                  subtitle={selectedBuyerIds.length > 0 ? `${selectedBuyerIds.length} selected` : 'Search and check the buyers you want'}
                  expanded={recipientMode === 'buyer_ids' && (
                    <BuyerPicker
                      buyers={filteredBuyers}
                      allBuyers={allBuyers}
                      search={buyerSearch}
                      setSearch={setBuyerSearch}
                      selectedIds={selectedBuyerIds}
                      onToggle={toggleBuyer}
                    />
                  )}
                />
                <RadioCard
                  selected={recipientMode === 'list_ids'}
                  onSelect={() => setRecipientMode('list_ids')}
                  title="Lists"
                  subtitle={selectedListIds.length > 0 ? `${selectedListIds.length} list(s) selected` : 'Send to every buyer in selected list(s)'}
                  expanded={recipientMode === 'list_ids' && (
                    <ListPicker
                      lists={allLists}
                      selectedIds={selectedListIds}
                      onToggle={toggleList}
                    />
                  )}
                />
              </div>

              {/* Outbound kill-switch banners — surface .env safety flags at the top of step 1
                  so the user understands BEFORE picking channels why something will simulate.
                  KILL_ALL_OUTBOUND wins (red, hard stop). SMS_DISABLED + circuit-breaker show
                  in amber. No banner when everything's live. */}
              {killState?.kill_all_outbound && (
                <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(184,84,80,0.12)', border: '1px solid rgba(184,84,80,0.4)', borderRadius: 8, fontSize: 12, color: '#B85450' }}>
                  ⛔ <strong>KILL_ALL_OUTBOUND is set in .env.</strong> Every email and SMS in this blast will SIMULATE — no real sends, no follow-ups schedule changes. Flip the env var to false and restart the backend to enable real sends.
                </div>
              )}
              {!killState?.kill_all_outbound && (killState?.sms_disabled || killState?.sms_circuit_breaker) && (
                <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(224, 165, 38,0.10)', border: '1px solid rgba(224, 165, 38,0.35)', borderRadius: 8, fontSize: 12, color: '#E0A526' }}>
                  ⚠ <strong>SMS is currently disabled.</strong>{' '}
                  {killState?.sms_circuit_breaker
                    ? `Circuit breaker tripped — ${killState.sms_circuit_break_reason || 'too many compliance failures'}. Restart the backend after fixing the underlying issue to clear it.`
                    : 'SMS_DISABLED=true in .env (set after the Twilio account was auto-suspended for A2P 10DLC non-compliance). Flip to false once Twilio is reinstated AND A2P registration is complete.'}
                  {' '}Email-only blasts work normally.
                </div>
              )}

              <SectionHeader label="How should we send it?" />
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <ChannelChip
                  selected={channels.includes('email')}
                  onClick={() => toggleChannel('email')}
                  label="Email"
                  icon="✉"
                  note="AI-personalized per buyer"
                  disabled={killState?.kill_all_outbound}
                  disabledReason={killState?.kill_all_outbound ? 'All outbound is paused (KILL_ALL_OUTBOUND).' : null}
                />
                <ChannelChip
                  selected={channels.includes('sms') && !killState?.sms_will_simulate}
                  onClick={() => !killState?.sms_will_simulate && toggleChannel('sms')}
                  label="SMS"
                  icon="📱"
                  note={killState?.sms_will_simulate ? 'Disabled' : 'Single bulk message'}
                  disabled={!!killState?.sms_will_simulate}
                  disabledReason={
                    killState?.kill_all_outbound ? 'All outbound is paused (KILL_ALL_OUTBOUND).'
                    : killState?.sms_circuit_breaker ? `SMS circuit breaker tripped — ${killState.sms_circuit_break_reason || 'compliance failures'}.`
                    : killState?.sms_disabled ? 'SMS is disabled in .env (SMS_DISABLED=true). Common cause: Twilio A2P 10DLC not yet registered.'
                    : null
                  }
                />
              </div>
              {channels.length === 0 && (
                <div style={{ fontSize: 11, color: '#B85450', marginBottom: 8 }}>
                  Pick at least one channel.
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <button onClick={onClose} style={ghostBtn}>Cancel</button>
                <button
                  onClick={fetchPreview}
                  disabled={!canPreview || previewLoading}
                  style={{ ...primaryBtn, flex: 2, opacity: (!canPreview || previewLoading) ? 0.5 : 1 }}
                >
                  {previewLoading ? 'Loading preview…' : 'Preview →'}
                </button>
              </div>
            </>
          )}

          {/* ── STEP 2: PREVIEW ───────────────────────────────────────────── */}
          {step === 'preview' && preview && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
                <Stat label="Recipients" value={preview.recipient_count} color="var(--teal)" />
                {channels.includes('email') && <Stat label="With email" value={preview.with_email} color="#6B6760" />}
                {channels.includes('sms')   && <Stat label="With SMS"   value={preview.with_phone} color="#14504A" />}
              </div>

              <SimulationStatus
                channels={channels}
                emailSim={preview.email_simulated}
                smsSim={preview.sms_simulated}
              />

              {preview.recipient_count === 0 ? (
                <div style={{ padding: 16, background: 'rgba(184,84,80,0.06)', border: '1px solid rgba(184,84,80,0.3)', borderRadius: 8, fontSize: 12, color: '#B85450' }}>
                  No buyers resolved with the current settings. Go back and broaden the recipient selection.
                </div>
              ) : (
                <>
                  <RecipientsPreview recipients={preview.recipients} truncated={preview.truncated} total={preview.recipient_count} />
                  <MessageEditor
                    channels={channels}
                    useCustom={useCustom}
                    setUseCustom={setUseCustom}
                    customSubject={customSubject} setCustomSubject={setCustomSubject}
                    customBody={customBody}       setCustomBody={setCustomBody}
                    customSms={customSms}         setCustomSms={setCustomSms}
                    sampleForBuyer={preview.sample?.for_buyer}
                  />
                </>
              )}

              <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
                <button onClick={() => setStep('configure')} style={ghostBtn}>← Back</button>
                <button
                  onClick={handleSend}
                  disabled={preview.recipient_count === 0}
                  style={{ ...primaryBtn, flex: 2, opacity: preview.recipient_count === 0 ? 0.5 : 1 }}
                >
                  Send to {preview.recipient_count} buyer{preview.recipient_count === 1 ? '' : 's'}
                </button>
              </div>
            </>
          )}

          {/* ── SENDING ────────────────────────────────────────────────── */}
          {step === 'sending' && <Spinner label={`Sending to ${preview?.recipient_count || ''} buyers…`} />}

          {/* ── DONE ───────────────────────────────────────────────────── */}
          {step === 'done' && result && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', background: 'rgba(74,124,89,0.08)', border: '1px solid rgba(74,124,89,0.3)', borderRadius: 10, marginBottom: 14 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#4A7C59', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>✓</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#4A7C59' }}>Marketing sent</div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                    Sent to {result.buyer_count} buyer{result.buyer_count === 1 ? '' : 's'} · logged to Communications
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
                {channels.includes('email') && <Stat label="Emails" value={`${result.email_count}${result.email_simulated ? ' sim' : ''}`} color="#6B6760" />}
                {channels.includes('sms')   && <Stat label="SMS"    value={`${result.sms_count}${result.sms_simulated ? ' sim' : ''}`}     color="#14504A" />}
                <Stat label="Follow-ups" value={result.followups_scheduled || 0} color="var(--teal)" />
              </div>

              {(result.email_simulated || result.sms_simulated) && (
                <div style={{ padding: '10px 12px', background: 'rgba(224, 165, 38,0.08)', border: '1px solid rgba(224, 165, 38,0.3)', borderRadius: 8, fontSize: 11, color: 'var(--gold)', marginBottom: 14 }}>
                  ⚠ Some sends were simulated. Add API keys (or finish A2P registration for SMS) to send for real.
                </div>
              )}

              <button onClick={onClose} style={{ ...primaryBtn, width: '100%' }}>Done</button>
            </>
          )}

          {/* ── ERROR ──────────────────────────────────────────────────── */}
          {step === 'error' && (
            <>
              <div style={{ padding: '12px 14px', background: 'rgba(184,84,80,0.08)', border: '1px solid rgba(184,84,80,0.3)', borderRadius: 8, marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#B85450' }}>Something went wrong</div>
                <div style={{ fontSize: 12, color: 'var(--gray-300)', marginTop: 4 }}>{error}</div>
              </div>
              <button onClick={() => setStep('configure')} style={{ ...primaryBtn, width: '100%' }}>Try Again</button>
            </>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function SectionHeader({ label }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
      {label}
    </div>
  );
}

function RadioCard({ selected, onSelect, title, subtitle, badge, expanded }) {
  return (
    <div
      onClick={onSelect}
      style={{
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${selected ? 'var(--teal)' : 'var(--navy-border)'}`,
        background: selected ? 'rgba(20, 80, 74,0.06)' : 'var(--navy)',
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 16, height: 16, borderRadius: '50%',
          border: `2px solid ${selected ? 'var(--teal)' : 'var(--navy-border)'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {selected && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--teal)' }} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{title}</div>
            {badge && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4, color: 'var(--teal)', background: 'rgba(20, 80, 74,0.15)', letterSpacing: '0.04em' }}>
                {badge}
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>{subtitle}</div>
        </div>
      </div>
      {expanded && <div onClick={e => e.stopPropagation()}>{expanded}</div>}
    </div>
  );
}

function ChannelChip({ selected, onClick, label, icon, note, disabled, disabledReason }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      title={disabled ? disabledReason : undefined}
      style={{
        flex: 1,
        padding: '10px 12px',
        borderRadius: 8,
        border: `1px solid ${selected ? 'var(--teal)' : 'var(--navy-border)'}`,
        background: disabled ? 'rgba(156,150,144,0.06)' : (selected ? 'rgba(20, 80, 74,0.08)' : 'var(--navy)'),
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        position: 'relative',
        color: selected ? 'var(--teal)' : 'var(--gray-300)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span>
        {selected && <span style={{ marginLeft: 'auto', fontSize: 13 }}>✓</span>}
      </div>
      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>{note}</div>
    </button>
  );
}

function BuyerPicker({ buyers, allBuyers, search, setSearch, selectedIds, onToggle }) {
  return (
    <div style={{ marginTop: 8 }}>
      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder={allBuyers === null ? 'Loading buyers…' : `Search ${allBuyers.length} buyers…`}
        style={{ ...input, width: '100%', marginBottom: 6 }}
      />
      <div style={{ maxHeight: 220, overflowY: 'auto', background: 'var(--navy-card)', borderRadius: 6, border: '1px solid var(--navy-border)' }}>
        {allBuyers === null && (
          <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>Loading…</div>
        )}
        {allBuyers !== null && buyers.length === 0 && (
          <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)' }}>No buyers match that search.</div>
        )}
        {buyers.map(b => (
          <label key={b.id} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '6px 10px', cursor: 'pointer',
            borderBottom: '1px solid rgba(232,227,218,0.06)',
            background: selectedIds.includes(b.id) ? 'rgba(20, 80, 74,0.06)' : 'transparent',
          }}>
            <input
              type="checkbox"
              checked={selectedIds.includes(b.id)}
              onChange={() => onToggle(b.id)}
              style={{ margin: 0 }}
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, color: 'var(--white)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {b.name}{b.company ? ` · ${b.company}` : ''}
              </div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 1 }}>
                {[b.email ? '✉' : null, b.phone ? '📱' : null, (b.notes || '').slice(0, 40)].filter(Boolean).join(' · ')}
              </div>
            </div>
          </label>
        ))}
      </div>
      {selectedIds.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--teal)', marginTop: 6, textAlign: 'right' }}>
          {selectedIds.length} selected
        </div>
      )}
    </div>
  );
}

function ListPicker({ lists, selectedIds, onToggle }) {
  if (lists === null) return <div style={{ padding: 12, fontSize: 11, color: 'var(--gray-500)', marginTop: 6 }}>Loading lists…</div>;
  // Filter to lists that actually have buyers — otherwise show empty state below
  const withBuyers = lists.filter(l => (l.buyer_count ?? l.member_count ?? 0) > 0 || (l.items && l.items.some(i => i.item_type === 'buyer')));
  return (
    <div style={{ marginTop: 8 }}>
      {lists.length === 0 || withBuyers.length === 0 ? (
        <div style={{ padding: 12, background: 'rgba(224, 165, 38,0.06)', border: '1px solid rgba(224, 165, 38,0.3)', borderRadius: 6, fontSize: 11, color: 'var(--gold)', lineHeight: 1.5 }}>
          No lists contain buyers yet. To populate a list: go to <strong>Buyers Database</strong>, select buyers, and use "Add to List" — then come back here.
        </div>
      ) : (
        <div style={{ background: 'var(--navy-card)', borderRadius: 6, border: '1px solid var(--navy-border)' }}>
          {lists.map(l => {
            const count = l.buyer_count ?? l.member_count ?? 0;
            const hasBuyers = count > 0;
            return (
              <label key={l.id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 10px', cursor: hasBuyers ? 'pointer' : 'not-allowed',
                opacity: hasBuyers ? 1 : 0.4,
                borderBottom: '1px solid rgba(232,227,218,0.06)',
                background: selectedIds.includes(l.id) ? 'rgba(20, 80, 74,0.06)' : 'transparent',
              }}>
                <input
                  type="checkbox"
                  checked={selectedIds.includes(l.id)}
                  onChange={() => hasBuyers && onToggle(l.id)}
                  disabled={!hasBuyers}
                  style={{ margin: 0 }}
                />
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: l.color || 'var(--teal)' }} />
                <div style={{ flex: 1, fontSize: 12, color: 'var(--white)', fontWeight: 600 }}>{l.name}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{count} buyer{count === 1 ? '' : 's'}</div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RecipientsPreview({ recipients, truncated, total }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        Recipients ({Math.min(recipients.length, 8)} of {total} shown)
      </div>
      <div style={{ background: 'var(--navy)', borderRadius: 8, padding: 4, maxHeight: 200, overflowY: 'auto' }}>
        {recipients.slice(0, 8).map((b, i) => (
          <div key={b.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderBottom: i < 7 && i < recipients.length - 1 ? '1px solid rgba(232,227,218,0.06)' : 'none' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, color: 'var(--white)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {b.name}{b.company ? ` · ${b.company}` : ''}
              </div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>
                {[b.email ? '✉' : null, b.phone ? '📱' : null].filter(Boolean).join(' · ')}
              </div>
            </div>
            {b.match_score != null && (
              <div style={{ flexShrink: 0, marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 4, background: 'rgba(20, 80, 74,0.15)', color: 'var(--teal)' }}>
                {b.match_score}
              </div>
            )}
          </div>
        ))}
      </div>
      {truncated && (
        <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>
          + {total - recipients.length} more not previewed
        </div>
      )}
    </div>
  );
}

function MessageEditor({
  channels, useCustom, setUseCustom,
  customSubject, setCustomSubject,
  customBody, setCustomBody,
  customSms, setCustomSms,
  sampleForBuyer,
}) {
  const [tab, setTab] = useState(channels.includes('email') ? 'email' : 'sms');
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Message {useCustom ? '(broadcast — same text to all)' : sampleForBuyer ? `(AI sample for ${sampleForBuyer})` : ''}
        </div>
        <label
          title={useCustom
            ? 'One Claude call to draft the sample, then broadcast that text as-is. Cheapest.'
            : 'Each buyer gets a custom Claude-drafted email. Costs ~$0.02-0.05 per recipient.'}
          style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 11, color: useCustom ? 'var(--teal)' : 'var(--gold)', fontWeight: 600 }}
        >
          <input
            type="checkbox"
            checked={useCustom}
            onChange={e => setUseCustom(e.target.checked)}
            style={{ margin: 0 }}
          />
          {useCustom ? '✓ Broadcast (cheap)' : 'AI-personalize per buyer (~$0.02-0.05 each)'}
        </label>
      </div>

      {channels.length > 1 && (
        <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
          {channels.includes('email') && <TabBtn active={tab === 'email'} onClick={() => setTab('email')}>Email</TabBtn>}
          {channels.includes('sms')   && <TabBtn active={tab === 'sms'}   onClick={() => setTab('sms')}>SMS</TabBtn>}
        </div>
      )}

      {tab === 'email' && channels.includes('email') && (
        <>
          <input
            value={customSubject}
            onChange={e => setCustomSubject(e.target.value)}
            disabled={!useCustom}
            placeholder="Subject"
            style={{ ...input, width: '100%', marginBottom: 4, opacity: useCustom ? 1 : 0.7 }}
          />
          <textarea
            value={customBody}
            onChange={e => setCustomBody(e.target.value)}
            disabled={!useCustom}
            rows={10}
            style={{ ...input, width: '100%', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5, opacity: useCustom ? 1 : 0.7 }}
          />
          {!useCustom && (
            <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 4, lineHeight: 1.5 }}>
              ⚠ AI-personalize mode — each buyer gets a fresh Claude-drafted email. Costs roughly $0.02-0.05 per recipient. The text above is a sample of what one buyer would receive. Untick to broadcast one shared message for a fraction of the cost.
            </div>
          )}
          {useCustom && (
            <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4, lineHeight: 1.5 }}>
              Edit the subject + body above. Everyone gets this exact text — one Claude call total (just to draft the sample you started from). Add personal touches if you want — variables like {'{name}'} aren't substituted, so write generically.
            </div>
          )}
        </>
      )}

      {tab === 'sms' && channels.includes('sms') && (
        <>
          <textarea
            value={customSms}
            onChange={e => setCustomSms(e.target.value)}
            disabled={!useCustom}
            rows={3}
            maxLength={1500}
            style={{ ...input, width: '100%', resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5, opacity: useCustom ? 1 : 0.7 }}
          />
          <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 4 }}>
            {useCustom ? `${customSms.length}/1500 chars · ` : 'Bulk SMS — same message to all recipients. '}
            Always include "Reply STOP to opt out" for compliance.
          </div>
        </>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      padding: '5px 10px', borderRadius: 6, border: 'none',
      background: active ? 'var(--navy-card)' : 'transparent',
      color: active ? 'var(--white)' : 'var(--gray-500)',
      fontSize: 11, fontWeight: 700, cursor: 'pointer',
    }}>{children}</button>
  );
}

function SimulationStatus({ channels, emailSim, smsSim }) {
  const emailIssue = channels.includes('email') && emailSim;
  const smsIssue   = channels.includes('sms')   && smsSim;
  if (!emailIssue && !smsIssue) {
    return (
      <div style={{ padding: '8px 12px', background: 'rgba(74,124,89,0.06)', border: '1px solid rgba(74,124,89,0.3)', borderRadius: 6, fontSize: 11, color: '#4A7C59', marginBottom: 14 }}>
        ✓ Sending live
      </div>
    );
  }
  return (
    <div style={{ padding: '8px 12px', background: 'rgba(224, 165, 38,0.08)', border: '1px solid rgba(224, 165, 38,0.3)', borderRadius: 6, fontSize: 11, color: 'var(--gold)', marginBottom: 14 }}>
      ⚠ {emailIssue && smsIssue ? 'Email AND SMS will SIMULATE (no Resend/Twilio keys).'
       : emailIssue ? 'Emails will SIMULATE (no Resend key).' : 'SMS will SIMULATE (no Twilio key or A2P not registered).'}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ padding: 12, background: 'var(--navy)', borderRadius: 8, textAlign: 'center' }}>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function Spinner({ label }) {
  return (
    <div style={{ padding: '60px 0', textAlign: 'center' }}>
      <div style={{ width: 36, height: 36, border: '3px solid var(--navy-border)', borderTopColor: 'var(--teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
      <div style={{ fontSize: 13, color: 'var(--gray-300)' }}>{label}</div>
    </div>
  );
}

// ── Shared styles ───────────────────────────────────────────────────────────
const overlay   = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const modalWrap = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 16, width: '100%', maxWidth: 680, maxHeight: '90vh', display: 'flex', flexDirection: 'column' };
const header    = { padding: '20px 28px 14px', borderBottom: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const closeBtn  = { background: 'none', border: 'none', color: 'var(--gray-500)', fontSize: 22, cursor: 'pointer', lineHeight: 1 };
const body      = { flex: 1, overflowY: 'auto', padding: '18px 28px 24px' };
const input     = { padding: '8px 10px', borderRadius: 6, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 12, outline: 'none', boxSizing: 'border-box' };
const primaryBtn = { flex: 1, padding: '10px 14px', borderRadius: 8, background: 'var(--teal)', border: '1px solid var(--teal)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const ghostBtn   = { flex: 1, padding: '10px 14px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-500)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
